import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import buttons from 'src/lib/ui/config/buttons';
import { Feature } from 'src/replicant/analytics';
import { PlayerProfile } from 'src/replicant/friends/friends.ruleset';
import { getPetSpeciesName } from 'src/replicant/pet/pet.getters';
import { SpeciesAvatar } from './SpeciesAvatar';

export type FriendListPlayerParams = PlayerProfile & {
  petName?: string;
  requestSent?: boolean;
  onAccept?: () => Promise<void>;
  onReject?: () => Promise<void>;
  onAdd?: () => Promise<void>;
  onTap?: () => void;
};

export type FriendListHeaderParams = {
  isHeader: true;
  label: string;
};

export type FriendListItemParams =
  | FriendListPlayerParams
  | FriendListHeaderParams;

export class FriendListItem extends View {
  private base: ImageScaleView;
  private tapArea: ButtonScaleView;
  private avatar: Avatar;
  private name: LangBitmapFontTextView;
  private species: LangBitmapFontTextView;
  private speciesAvatar: SpeciesAvatar;
  private requested: ImageScaleView;
  private btnAdd: ButtonScaleViewWithText;
  private btnAccept: ButtonScaleView;
  private btnReject: ButtonScaleView;
  private btnView: ButtonScaleViewWithText;
  private headerLabel: LangBitmapFontTextView;
  private onAccept?: () => Promise<void>;
  private onReject?: () => Promise<void>;
  private onAdd?: () => Promise<void>;
  private onTap?: () => void;

  constructor(opts?: ViewOpts) {
    super({
      offsetX: 10,
      width: 498,
      height: 114,
      ...opts,
    });

    this.base = new ImageScaleView({
      superview: this,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      width: this.style.width,
      height: this.style.height,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 34, right: 34 },
        vertical: { top: 34, bottom: 34 },
      },
      imgScale: DOWNSCALE_FACTOR,
    });

    this.avatar = new Avatar({
      superview: this,
      width: 78,
      height: 78,
      x: 16,
      y: 15,
      centerOnOrigin: false,
    });

    this.name = new LangBitmapFontTextView({
      superview: this,
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'top',
      x: 105,
      y: 25,
      width: 200,
      height: 48,
    });

    // species name
    this.species = new LangBitmapFontTextView({
      superview: this,
      size: 22,
      font: 'Body',
      color: '#7191AB',
      x: this.name.style.x,
      y: 63,
      width: this.name.style.width,
      height: 34,
      align: 'left',
    });

    this.speciesAvatar = new SpeciesAvatar({
      superview: this,
      x: this.avatar.style.x + this.avatar.style.width / 2 + 5,
      y: this.avatar.style.y + this.avatar.style.height / 2 + 5,
      scale: 0.3,
    });

    // Tap area covers the whole row but sits behind action buttons
    this.tapArea = new ButtonScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      visible: false,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.VIEW_REQUEST_PROFILE,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.onTap?.();
      },
    });

    this.btnAdd = new ButtonScaleViewWithText({
      superview: this,
      ...buttons.secondary,
      pressedOffsetY: 5,
      width: 120,
      height: 60,
      x: this.style.width - 80,
      y: this.style.height / 2,
      centerOnOrigin: true,
      labelOffsetX: -2,
      labelOffsetY: -1,
      fontSize: 22,
      font: 'Body',
      localeText: () => i18n('friends.add'),
      visible: false,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.ADD_FRIEND,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.onAdd?.();
      },
    });

    this.btnReject = new ButtonScaleView({
      superview: this,
      image: 'assets/ui/friends/reject.png',
      imagePressed: 'assets/ui/friends/reject_pressed.png',
      imageDisabled: 'assets/ui/friends/reject_pressed.png',
      scaleMethod: 'stretch',
      pressedOffsetY: 5,
      width: 78,
      height: 55,
      x: 310,
      y: 29,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.REJECT_REQUEST,
      },
      onClick: async () => {
        sounds.playSound('cancel');
        await this.onReject?.();
      },
    });

    this.btnAccept = new ButtonScaleView({
      superview: this,
      image: 'assets/ui/friends/accept.png',
      imagePressed: 'assets/ui/friends/accept_pressed.png',
      imageDisabled: 'assets/ui/friends/accept_pressed.png',
      scaleMethod: 'stretch',
      pressedOffsetY: 5,
      width: 78,
      height: 55,
      x: 400,
      y: this.btnReject.style.y,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.ACCEPT_REQUEST,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.onAccept?.();
      },
    });

    this.btnView = new ButtonScaleViewWithText({
      superview: this,
      ...buttons.secondary,
      pressedOffsetY: 5,
      width: 100,
      height: 60,
      x: this.style.width - 70,
      y: this.style.height / 2,
      centerOnOrigin: true,
      labelOffsetX: -2,
      labelOffsetY: -1,
      fontSize: 22,
      font: 'Body',
      localeText: () => i18n('friendsList.view'),
      visible: false,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.VIEW_FRIEND_PROFILE,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.onTap?.();
      },
    });

    this.requested = new ImageScaleView({
      superview: this,
      image: 'assets/ui/friends/requested_label.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 20, bottom: 20 },
      },
      x: this.style.width - 80,
      y: this.style.height / 2,
      width: 120,
      height: 42,
      centerOnOrigin: true,
    });

    new LangBitmapFontTextView({
      superview: this.requested,
      font: 'Body',
      color: '#7191AB',
      x: this.requested.style.width / 2,
      y: this.requested.style.height / 2,
      centerOnOrigin: true,
      width: this.requested.style.width - 20,
      height: this.requested.style.height,
      size: 24,
      align: 'center',
      verticalAlign: 'center',
      localeText: () => i18n('friends.requested'),
    });

    this.headerLabel = new LangBitmapFontTextView({
      superview: this,
      width: this.style.width,
      height: 50,
      x: 0,
      y: 0,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Body',
      color: '#AE7E4A',
      visible: false,
    });
  }

  public setup(params: FriendListItemParams) {
    if ('isHeader' in params) {
      this.setupHeader(params);
      return;
    }
    this.setupPlayer(params);
  }

  private setupHeader(params: FriendListHeaderParams) {
    // Hide all player elements
    this.base.hide();
    this.avatar.hide();
    this.name.hide();
    this.species.hide();
    this.speciesAvatar.hide();
    this.tapArea.updateOpts({ visible: false });
    this.btnAdd.hide();
    this.btnAccept.hide();
    this.btnReject.hide();
    this.btnView.hide();
    this.requested.hide();

    // Show header label
    this.headerLabel.show();
    this.headerLabel.localeText = () => params.label;
    this.updateOpts({ height: 50 });
  }

  private setupPlayer(params: FriendListPlayerParams) {
    // Hide header, show player elements
    this.headerLabel.hide();
    this.updateOpts({ height: 114 });

    this.name.show();
    this.base.show();
    this.avatar.show();
    this.name.localeText = () => params.name;
    this.avatar.updateImage(params.profilePicture);
    this.avatar.setFrame(params.frame ?? 'default');

    if (params.petName) {
      const petName = params.petName;
      this.species.localeText = () => petName;
    } else {
      this.species.localeText = () => getPetSpeciesName(user(), params.species);
    }

    this.speciesAvatar.setSpecies(params.species);

    this.onAccept = params.onAccept;
    this.onReject = params.onReject;
    this.onAdd = params.onAdd;
    this.onTap = params.onTap;

    const hasActionCallbacks =
      !!this.onAdd || !!this.onAccept || !!this.onReject;
    const showViewButton =
      !!this.onTap && !hasActionCallbacks && !params.requestSent;

    this.tapArea.updateOpts({
      visible: !!this.onTap,
    });
    this.btnAdd.updateOpts({
      visible: !!this.onAdd,
    });
    this.btnAccept.updateOpts({
      visible: !!this.onAccept,
    });
    this.btnReject.updateOpts({
      visible: !!this.onReject,
    });
    this.btnView.updateOpts({
      visible: showViewButton,
    });

    if (params.requestSent) {
      this.btnAdd.hide();
      this.btnAccept.hide();
      this.btnReject.hide();
      this.btnView.hide();
      this.requested.show();
    } else {
      this.requested.hide();
    }

    const hasVisibleButtons =
      this.btnAdd.style.visible ||
      this.btnAccept.style.visible ||
      this.btnReject.style.visible ||
      this.btnView.style.visible ||
      this.requested.style.visible;

    let labelWidth = this.style.width - 130;
    if (hasVisibleButtons) {
      labelWidth -= 120; // account for buttons on the right side
    }

    this.name.updateOpts({
      width: labelWidth,
    });

    this.species.updateOpts({
      width: labelWidth,
    });
  }
}
